import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { defaultTemplates, templateMeta } from "@/lib/defaultEmailTemplates";

export interface EmailTemplate {
  id?: string;
  type: string;
  subject: string;
  body: string;
  heading: string;
  cta_text: string;
  show_features: boolean;
  feature_lines: string[];
  footer_text?: string;
  is_active: boolean;
  isDefault?: boolean;
}

interface EmailTemplateRow {
  id: string;
  type: string;
  subject: string;
  body: string;
  heading: string | null;
  cta_text: string | null;
  show_features: boolean | null;
  feature_lines: string[] | null;
  footer_text: string | null;
  is_active: boolean | null;
  organization_id: string;
  updated_at: string | null;
}

export function useEmailTemplates(orgId: string | undefined) {
  const qc = useQueryClient();

  const { data: dbTemplates = [], isLoading } = useQuery({
    queryKey: ["email-templates", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates" as any)
        .select("*")
        .eq("organization_id", orgId!);
      if (error) throw error;
      return (data ?? []) as EmailTemplateRow[];
    },
    enabled: !!orgId,
  });

  // Merge DB templates with defaults
  const templates: EmailTemplate[] = Object.keys(templateMeta).map((type) => {
    const dbRow = dbTemplates.find((t) => t.type === type);
    if (dbRow) {
      return {
        id: dbRow.id,
        type: dbRow.type,
        subject: dbRow.subject,
        body: dbRow.body,
        heading: dbRow.heading ?? "",
        cta_text: dbRow.cta_text ?? "Bekräfta",
        show_features: dbRow.show_features ?? true,
        feature_lines: dbRow.feature_lines ?? [],
        footer_text: dbRow.footer_text,
        is_active: dbRow.is_active ?? true,
        isDefault: false,
      };
    }
    const def = defaultTemplates[type];
    return {
      type,
      subject: def?.subject ?? "",
      body: def?.body ?? "",
      heading: "",
      cta_text: "Bekräfta",
      show_features: false,
      feature_lines: [] as string[],
      is_active: true,
      isDefault: true,
    };
  });

  const upsertMutation = useMutation({
    mutationFn: async (template: Omit<EmailTemplate, "id" | "isDefault">) => {
      const payload: Record<string, unknown> = {
        subject: template.subject,
        body: template.body,
        heading: template.heading,
        cta_text: template.cta_text,
        show_features: template.show_features,
        feature_lines: template.feature_lines,
        footer_text: template.footer_text ?? null,
        is_active: template.is_active,
        updated_at: new Date().toISOString(),
      };
      const existing = dbTemplates.find((t) => t.type === template.type);
      if (existing) {
        const { error } = await supabase
          .from("email_templates" as any)
          .update(payload as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("email_templates" as any)
          .insert({ ...payload, organization_id: orgId, type: template.type } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-templates", orgId] });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (type: string) => {
      const existing = dbTemplates.find((t) => t.type === type);
      if (existing) {
        const { error } = await supabase
          .from("email_templates" as any)
          .delete()
          .eq("id", existing.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-templates", orgId] });
    },
  });

  return {
    templates,
    isLoading,
    upsertTemplate: upsertMutation.mutateAsync,
    resetTemplate: resetMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
  };
}
